import { useState, useEffect } from "react";
import type { JSX } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { usePermissions } from "react-admin";
import { hasPermission, type Permission } from "../../auth/HasPermission";
import { IfPermission } from "../../auth/IfPermission";
import type { PublicationLimitsState } from "./usePublicationLimits";
import { committedData, validateLimitInput, formatUpdatedAt } from "./PublicationLimitsCard.logic";

const HINT_TEXT =
  "Сайт, достигший лимита, исключается из подбора для этого типа публикации. Изменение действует на новые публикации сразу.";

interface PublicationLimitsCardProps {
  state: PublicationLimitsState;
  onSave: (values: { articleMaxPosts: number; sentenceMaxPosts: number }) => void;
  onRetry: () => void;
}

export function PublicationLimitsCard({ state, onSave, onRetry }: PublicationLimitsCardProps) {
  const { permissions } = usePermissions<Permission[]>();
  const canManage = hasPermission(permissions, "settings.manage");
  const data = committedData(state);

  const [articleDraft, setArticleDraft] = useState("");
  const [sentenceDraft, setSentenceDraft] = useState("");
  const [articleFieldError, setArticleFieldError] = useState<string | undefined>(undefined);
  const [sentenceFieldError, setSentenceFieldError] = useState<string | undefined>(undefined);

  const syncedUpdatedAt = state.kind === "data" ? state.updatedAt : null;
  useEffect(() => {
    if (state.kind === "data") {
      setArticleDraft(String(state.articleMaxPosts));
      setSentenceDraft(String(state.sentenceMaxPosts));
      setArticleFieldError(undefined);
      setSentenceFieldError(undefined);
    }
    // Re-seeds the drafts only when a fresh committed value arrives (initial
    // load or a successful save); left untouched while saving or on error so
    // the values the admin entered are not lost (docs/screens/Dashboard.md).
  }, [syncedUpdatedAt]);

  function handleSave() {
    const articleError = validateLimitInput(articleDraft);
    const sentenceError = validateLimitInput(sentenceDraft);
    setArticleFieldError(articleError);
    setSentenceFieldError(sentenceError);
    if (articleError === undefined && sentenceError === undefined) {
      onSave({ articleMaxPosts: Number(articleDraft), sentenceMaxPosts: Number(sentenceDraft) });
    }
  }

  let content: JSX.Element;
  if (state.kind === "loading") {
    content = (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress size={24} />
      </Box>
    );
  } else if (data === null) {
    content = (
      <Alert
        severity="error"
        action={
          <Button size="small" onClick={() => onRetry()}>
            Повторить
          </Button>
        }
      >
        {state.kind === "error" ? state.message : ""}
      </Alert>
    );
  } else {
    const isSaving = state.kind === "saving";
    content = (
      <Box>
        {state.kind === "error" && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {state.message}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" mb={2}>
          Обновлено: {formatUpdatedAt(data.updatedAt)}
          {data.updatedByName !== null ? `, ${data.updatedByName}` : ""}
        </Typography>

        {canManage ? (
          <TextField
            label="Максимум публикаций (Articles)"
            type="number"
            value={articleDraft}
            error={articleFieldError !== undefined}
            helperText={articleFieldError}
            disabled={isSaving}
            onChange={(event) => setArticleDraft(event.target.value)}
            size="small"
            fullWidth
            sx={{ mb: 2 }}
          />
        ) : (
          <Typography variant="body1" mb={2}>
            Максимум публикаций (Articles): {data.articleMaxPosts}
          </Typography>
        )}

        {canManage ? (
          <TextField
            label="Максимум публикаций (Homepage + Sitewide)"
            type="number"
            value={sentenceDraft}
            error={sentenceFieldError !== undefined}
            helperText={sentenceFieldError}
            disabled={isSaving}
            onChange={(event) => setSentenceDraft(event.target.value)}
            size="small"
            fullWidth
            sx={{ mb: 2 }}
          />
        ) : (
          <Typography variant="body1" mb={2}>
            Максимум публикаций (Homepage + Sitewide): {data.sentenceMaxPosts}
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          {HINT_TEXT}
        </Typography>

        {canManage && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
          >
            Сохранить лимиты заспамленности
          </Button>
        )}
      </Box>
    );
  }

  return (
    <IfPermission permission="settings.view">
      <Box minWidth={300} maxWidth={480}>
        <Card>
          <CardContent>
            <Typography variant="h6" mb={2}>
              Лимиты заспамленности
            </Typography>
            {content}
          </CardContent>
        </Card>
      </Box>
    </IfPermission>
  );
}
